import { Stack } from '@mui/material';
import { AppContext } from '../../common/AppContext';
import { AbstractDialog } from '../../components/AbstractDialog';
import { Button } from '../../components/Button';
import { Dialog } from '../../components/dialogs/Dialog';
import {
  OrganizationSelector,
  OrganizationSelectorItem,
} from '../../components/selectors/OrganizationSelector';
import { EmployeeTable } from '../../components/table/EmployeeTable';
import { TextField } from '../../components/TextField';
import { ImpersonationOrgList, impersonationOrgLabel } from '../../data/users/ImpersonationOrgList';
import { SwitchUserList } from '../../data/users/SwitchUserList';
import { withLocale, WithLocaleProps } from '../../providers/LocaleProvider';
import SearchIcon from '../../resources/images/icons-search.svg';
import { trimToLowercase } from '../../utilities';

interface SwitchUserDialogProps extends WithLocaleProps {
  organization?: any;
  theme?: any;
  user?: any;
  onClose?: () => void;
  onSwitchUser?: (e: any, user: any) => void;
  [key: string]: unknown;
}

class SwitchUserDialogBase extends AbstractDialog<SwitchUserDialogProps> {
  static contextType = AppContext;
  declare context: React.ContextType<typeof AppContext>;

  constructor(props: any) {
    super(props);

    // The organization to show first is known synchronously — it is the one being viewed — so the
    // employee fetch does not have to wait on the organization list. Both the id and the label come off
    // props.organization, and the label goes through the same helper the fetched options use, so this
    // default cannot disagree with its own entry in the list.
    this.state = {
      ...this.state,
      users: null,
      orgs: null,
      selectedOrg: props.organization
        ? {
            organizationId: props.organization.organizationId,
            label: impersonationOrgLabel(props.organization.name, props.organization.isDemo),
          }
        : null,
      switching: false,
      userSearch: null,
      tableHeight: this.calcTableSize(),
    };

    window.addEventListener('resize', (e) => {
      this.setState({ tableHeight: this.calcTableSize() });
    });
  }

  // Guards against a stale employee response overwriting a newer organization's.
  latestUsersRequestId = 0;

  componentDidMount() {
    const selectedOrg = (this.state as any).selectedOrg;
    if (selectedOrg) this.loadUsers(selectedOrg.organizationId);
    // Only a CanChangeOrg user gets the picker, so nobody else needs the organization list.
    if (this.props.user?.canChangeOrg) this.loadOrgs();
  }

  calcTableSize() {
    return window.innerHeight - 400;
  }

  loadOrgs() {
    ImpersonationOrgList.getOrgs(this.context as any)
      .then((r) => this.setState({ orgs: r.data.orgs }))
      .catch((error) => {
        //Todo: handle error
        //alert('error 56564: ' + error);
      });
  }

  loadUsers(organizationId: string) {
    // Only the newest request may write to state. The picker is disabled while a load is in flight, so
    // two are not reachable from the UI today, but that invariant is one `disabled` expression away from
    // gone — and a stale write here would list one organization's people under another's name.
    const requestId = ++this.latestUsersRequestId;
    const isStale = () => requestId !== this.latestUsersRequestId;

    this.setLoading('employees' as any, true);
    this.setState({ users: null });

    SwitchUserList.getUsers(this.context as any, organizationId)
      .then((r) => {
        if (!isStale()) this.setState({ users: r.data.users });
      })
      .catch(() => {
        // AxiosClient already raises the error to the user. Settle on an empty list rather than leaving
        // `users` null, which disables the search box AND the organization picker — one failed
        // organization would otherwise brick the dialog until it is closed and reopened.
        if (!isStale()) this.setState({ users: [] });
      })
      .finally(() => {
        if (!isStale()) this.setLoading('employees' as any, false);
      });
  }

  onOrgChange(e: any) {
    const org = e.target.value as OrganizationSelectorItem;
    this.setState({ selectedOrg: org });
    this.loadUsers(org.organizationId);
  }

  onUserSelect(e: any, user: any) {
    this.setLoading('switching' as any, true);
    this.setState({ switching: true }, () => {
      this.props.onSwitchUser?.(e, user);
    });
  }

  onUserSearch(e: any) {
    this.setState({ userSearch: e.target.value });
  }

  render() {
    const { organization, theme, user, onClose, translate } = this.props;
    const { switching, users, orgs, selectedOrg, userSearch, tableHeight } = this.state as any;

    const filterItems = [
      {
        id: 'search',
        field: 'profilePicFullNameJobTitle',
        operator: 'contains',
        value: userSearch,
      },
    ];

    const filteredUser = users
      ? users?.filter((u: any) => {
          const keyword = trimToLowercase(userSearch);
          const userName = trimToLowercase(u?.fullName);
          return userName?.includes(keyword);
        })
      : null;

    return (
      <Dialog
        open={true}
        theme={theme as any}
        onClose={onClose as any}
        fullWidth
        maxWidth='md'
        className='klaro-switchuserdialog'
        title={
          <Stack direction='row' spacing={2} justifyContent='space-between' sx={{ padding: 0 }}>
            <div>{translate('switchUser.title')}</div>
            {user && user.loggedInUser && (
              <Button
                label={translate('switchUser.switchTo', {
                  name: user.loggedInUser.basicInformation.fullName,
                })}
                tooltip={translate('switchUser.switchToLoggedIn', {
                  name: user.loggedInUser.basicInformation.fullName,
                  org: user.loggedInUser.basicInformation.organizationName,
                })}
                disabled={switching}
                size='md'
                onClick={(e: any) => this.onUserSelect(e, null)}
                sx={{ marginRight: '12px' }}
              />
            )}
          </Stack>
        }
        actions={[
          {
            label: translate('common.close'),
            variant: 'filled',
            onClick: onClose as any,
            disabled: switching,
          },
        ]}
      >
        {this.renderLoadingBar()}

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ marginBottom: '16px' }}>
          <TextField
            className='klaro-switchuserdialog_search'
            value={userSearch}
            label={translate('common.search')}
            placeholder={translate('switchUser.searchUser')}
            fullWidth={true}
            disabled={!users || switching || user === null}
            autoComplete={false}
            leadingIcon={<SearchIcon />}
            onChange={this.onUserSearch.bind(this)}
          />
          {user && user.canChangeOrg && (
            <OrganizationSelector
              className='klaro-switchuserdialog_orgselect'
              value={selectedOrg}
              items={orgs ?? []}
              fullWidth={true}
              sx={{
                width: '100%',
                marginBottom: '20px',
                '& fieldset legend span': {
                  display: 'none',
                },
              }}
              onChange={this.onOrgChange.bind(this)}
              disabled={!users || switching || (user && !user.canChangeOrg)}
            />
          )}
        </Stack>

        <div className='klaro-switchuserdialog_table'>
          <EmployeeTable
            pagination={false}
            showHeader={false}
            showFooter={false}
            theme={theme as any}
            filterItems={filterItems as any}
            columns={[
              { type: (EmployeeTable as any).columns.profilePicFullNameJobTitle },
              { type: (EmployeeTable as any).columns.userDetails, visibleFromBreakpoint: 'md' },
            ]}
            sortModel={[
              { field: 'userDetails', sort: 'desc' },
              { field: 'profilePicFullNameJobTitle', sort: 'asc' },
            ]}
            // rows={users}
            rows={filteredUser}
            paper={false}
            autoHeight={false}
            disabled={switching}
            hideFirstLastBorder={false}
            onEmployeeSelect={this.onUserSelect.bind(this) as any}
            sx={{ height: { xs: tableHeight + 'px', md: '400px' } }}
          />
        </div>
      </Dialog>
    );
  }
}

export const SwitchUserDialog = withLocale(SwitchUserDialogBase);
